import { useRef, useCallback, useEffect } from "react";
import { useExtractions } from "../../../hooks/useExtractions";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../../components/ui/table";
import { useNavigate } from "react-router-dom";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import { ExtractionSchema } from "../../../models/extraction";
import { z } from "zod";
import { formattedDate } from "../../../lib/utils";

export const ExtractionsPage = () => {
  const navigate = useNavigate();
  const {
    extractions,
    loading,
    fetchExtractions,
    hasMoreLogs,
    fetchExtraction,
  } = useExtractions({ fetchOnLoad: true });
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const pendingExtractions = extractions.filter(
        (extraction) =>
          extraction.status === "pending" || extraction.status === "processing",
      );

      if (pendingExtractions.length > 0) {
        for (const extraction of pendingExtractions) {
          fetchExtraction(extraction.extractionId);
        }
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, [extractions, fetchExtraction]);

  const lastRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreLogs) {
          fetchExtractions();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMoreLogs, fetchExtractions],
  );

  return (
    <div className="h-screen flex flex-col w-full mx-auto">
      <div className="px-4 mx-auto flex flex-col w-full max-w-4xl mt-24">
        <div className="text-2xl">Extractions</div>
        {loading ? (
          <div className="flex justify-center items-center mt-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="mt-8 max-h-[75vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Files</TableHead>
                  <TableHead>Extaction ID</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {extractions.map((item, index) => (
                  <TableRow
                    className={`${
                      item.status === "completed" ? "cursor-pointer" : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (item.status !== "completed") {
                        return;
                      }
                      navigate(
                        `/dashboard/extraction/view/${item.extractionId}`,
                      );
                    }}
                    key={index}
                    ref={index === extractions.length - 1 ? lastRowRef : null}
                  >
                    <TableCell>
                      {item.files.join(", ").substring(0, 60)}
                    </TableCell>
                    <TableCell>{item.extractionId}</TableCell>
                    <TableCell>{formattedDate(item.createdAt)}</TableCell>
                    <StatusCell extraction={item} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusCell = ({
  extraction,
}: {
  extraction: z.infer<typeof ExtractionSchema>;
}) => {
  const status = extraction.status;
  const statusColor =
    status === "completed"
      ? "bg-green-500"
      : status === "processing" || status === "pending"
        ? "bg-yellow-500"
        : "bg-red-500";
  return (
    <TableCell className="capitalize flex items-center gap-2">
      <span className="relative flex h-2 w-2">
        {(status === "processing" || status === "pending") && (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${statusColor}`}
        ></span>
      </span>
      {status}
    </TableCell>
  );
};
